import { TranslateService } from "@ngx-translate/core";
import { Account, AccountType, CommType, OptionStrategy, OrderDuration, OrderType, Quote, SecurityType, TradingEntitlements, TradingOrderBase } from "../../models";
import { ORDER_ACTIONS, TradingConstants, getValueOrEmptyString, isAveragePriceAccount } from "../../utils";
import { Inject, Injectable, Optional } from "@angular/core";
import { DatePipe, registerLocaleData } from "@angular/common";
import es from "@angular/common/locales/es";
import moment_ from 'moment';


const linebreak = (navigator.appVersion.indexOf("Win") != -1)?'\r\n':'\n';
const moment = moment_;

@Injectable()
export class OptionTradingUtils {
    optionLegChainTypes: string[] = [];
    optionLegQuoteDesc: string[] = [];
    optionLegQuoteCusip: string[] = [];
    optionLegExpirationDates: Date[] = [];
    optionLegStrikePrices: number[] = [];
    optionLegQty:string[] = [];
    origOptionLegQty:string[] = [];
    optionLegTransactionTypes:string[] = [];
    optionLegSymbol:string[] = [];
    optionLegAccountTypes:string[] = [];
    optionLegStrLvsQty:string[] = [];
    hasQuantityEdited: boolean = false;


    commaDelimiterRE: RegExp = /\B(?=(\d{3})+(?!\d))/g;
    datePipe: DatePipe = new DatePipe('en-US');

    constructor(private translateService: TranslateService, @Optional() @Inject('AppSettings') appSettings: any) {
        if (appSettings?.language === 'es') {
            registerLocaleData( es );
        }
    }
    
    public getOrderTitleStyleColor(orderTitleStyle:string):string{
        let color:string='black';
        if(orderTitleStyle === 'tr-OrderTitle_Buy') {
            color = '#008338';
        } else if(orderTitleStyle === 'tr-OrderTitle_Sell') {
            color = '#d81b60'
        }
        return color;
    }

    public getAccountName(account:Account,accountName:string): string {
        return 'Account: ' + accountName +(!!account.ip?'   /   ' +' IP#: ' + getValueOrEmptyString(account.ipName) + account.ip:'');
    }

    public getOrderPlacedon(username:string, approvalRequired:boolean,orderDate:string,orderTime:string,orderAMPM:string,orderTimeZone:string): string {
        let _orderPlacedon = "";
        if (!!orderDate && orderTime && orderAMPM && orderTimeZone) {
            if (!approvalRequired) {
                _orderPlacedon = 'Order successfully submitted on ' + orderDate + ' at ' + orderTime + ' ' + orderAMPM + ' ' + orderTimeZone + (!!username?"/ Entered by " + username:'');
    
            } else {
                _orderPlacedon = 'Order submitted for approval on ' + orderDate + ' at ' + orderTime + ' ' + orderAMPM + ' ' + orderTimeZone + (!!username?"/ Entered by " + username:'');
            }
    
        } else {
            _orderPlacedon = (!!username?"Entered by " + username:'');
        }
        return _orderPlacedon;
    }

    public getOrderProcessing(isOrderSuccessfullySubmitted: boolean): string {
        let _orderProcessing = "";
        if (isOrderSuccessfullySubmitted) {
          _orderProcessing = "The following Order has been sent for Processing";
        }
        return _orderProcessing;
    }

    public getOrderStatus(isOrderSuccessfullySubmitted:boolean, hasViolations:boolean): string {
        let _orderstatus = "";

        if (!isOrderSuccessfullySubmitted && !hasViolations) {
            _orderstatus = "Option Order Review";
        } else if (!isOrderSuccessfullySubmitted && hasViolations) {
            _orderstatus = "Option Order Review";
        } else if (isOrderSuccessfullySubmitted) {
            _orderstatus = "Option Order Confirmation";
        }
        return _orderstatus;
    }

    public getTransactionType(transactionType: string,account?:Account,entitlements?:TradingEntitlements): string {
        if (!!entitlements?.isEntitledToComplexOption && isAveragePriceAccount(account?.acctNum)) {
            if (transactionType === 'BUY_CALL_TO_CLOSE' || transactionType === 'BUY_PUT_TO_CLOSE' 
                || transactionType === 'CLOSE_EXISTING_CALL' || transactionType === 'CLOSE_EXISTING_PUT') {
                transactionType += '_NOT_IN_ACCOUNT';
            } 
        }
    
        if (!!entitlements?.isEntitledToComplexOption) {
            let type = '';
            this.translateService.get(transactionType).subscribe((text) => {
                type = text;
            });
            return type;
        } else {
            switch (transactionType) {
                case "BUY": return 'Buy';
                case "SELL": return 'Sell';
                case "BUY_CALL_TO_OPEN": return "Buy Calls to Open";
                case "BUY_PUT_TO_OPEN": return "Buy Puts to Open";
                case "SELL_CALL_TO_OPEN": return "Sell Calls to Open";
                case "SELL_PUT_TO_OPEN": return "Sell Puts to Open";
                case "BUY_CALL_TO_CLOSE": return "Close Calls";
                case "BUY_PUT_TO_CLOSE": return "Close Puts";
                case "CLOSE_EXISTING_CALL": return "Close Calls";
                case "CLOSE_EXISTING_PUT": return "Close Puts";
                case "SELL_COVERED_CALLS": return "Sell Covered Calls";
                case "BUY_CALL_TO_CLOSE_NOT_IN_ACCOUNT": return "Close Calls Not in Account";
                case "BUY_PUT_TO_CLOSE_NOT_IN_ACCOUNT": return "Close Puts Not in Account";
                case "SELL_CALL_TO_CLOSE_NOT_IN_ACCOUNT": return "Close Calls Not in Account";
                case "SELL_PUT_TO_CLOSE_NOT_IN_ACCOUNT": return "Close Puts Not in Account";
                default:
                    return "";
            }
        }

    };

    private splitRegExFixed(value:number) {
        let split:string[]=[];
        if (value === null || value === undefined) {
            return '';
        }
        if (value === 0 || !!value) {
          split = value.toFixed(2).split('.');
        }
        let split0 = '';
        let split1 = '';
        let decimal = '';
        if (!!split[0]) {
            split0 = split[0];
        }
        if (!!split[1]) {
          split1 = split[1];
          decimal = '.';
        }
        return (split0).replace(this.commaDelimiterRE, ',') + decimal + split1;
    }

    private splitRegExUnFixed(value:number) {
        let split:string[]=[];
        if (value === 0) {
            return '0.00';
        }
        if (value === null || value === undefined) {
            return '';
        }
        if (!!value) {
          split = value.toString().split('.');
        }
        let split0 = '';
        let split1 = '';
        let decimal = '';
        if (!!split[0]) {
            split0 = split[0];
        }
        if (!!split[1]) {
          split1 = split[1];
          decimal = '.';
        }
        return (split0).replace(this.commaDelimiterRE, ',') + decimal + split1;
    }
    
    public getOrderDescription(order: any,entitlements:TradingEntitlements): string {
        let _orderDescription = this.getTransactionType(order.transactionType);

        if (!!order.strategy && entitlements.isEntitledToComplexOption) {
            return 'Strategy: ' + this.getStrategy(order);
        }
        
        if (['BUY_CALL_TO_OPEN','BUY_PUT_TO_OPEN','SELL_CALL_TO_OPEN','SELL_PUT_TO_OPEN','SELL_COVERED_CALLS'].includes(order.transactionType)) {
            _orderDescription += ' - ' + this.splitRegExUnFixed(order.ordQty) + ' Contract(s) of ' + order.quote.symId;
        } else if (order.transactionType.substring(0,3)==='BUY') {
            _orderDescription += ' - Buy ' + this.splitRegExUnFixed(order.ordQty) + ' Contract(s) of ' + order.quote.symId;
        } else {
            _orderDescription += ' - Sell ' + this.splitRegExUnFixed(order.ordQty) + ' Contract(s) of ' + order.quote.symId;
        }

        return _orderDescription;
    }

    public processRefNumTranslation() {
        let _refNum = '';

        this.translateService.get('messages.RefNum').subscribe((translation: string) => {
            _refNum+= translation;

        });
        return getValueOrEmptyString(_refNum);
    }

    public processRefNum(order: any, entitlements: TradingEntitlements) {
        if (!!order.chkgActyRefNum) {
            return getValueOrEmptyString(order.chkgActyRefNum);
        } else if (entitlements.isRetailUser && (!!order.trdOrdCnfrmCd)) {
            return getValueOrEmptyString(order.trdOrdCnfrmCd);
        }
    }

    public processStatus(status) {
        let _status = '';
        this.translateService.get('ORDER_STATUS.STATUS.' + (!!status? status:'EMPTY')).subscribe((translation: string) => {
            _status+= translation;
        });
        return getValueOrEmptyString(_status);
    }

    public getStrategy(order:any) {
        switch(order.strategy){
            case('SINGLE'): return 'Single Order';
            case('BUY_WRITE'): return 'Buy-Write';
            case('CALENDAR_SPREAD'): return 'Calendar Spread';
            case('COLLAR'): return 'Collar';
            case('DIAGONAL_SPREAD'): return 'Diagonal Spread';
            case('MARRIED_PUT'): return 'Married-Put';
            case('RATIO'): return 'Ratio';
            case('ROLL'): return 'Roll';
            case('STRADDLE'): return 'Straddle';
            case('STRANGLE'): return 'Strangle';
            case('VERTICAL_SPREAD'): return 'Vertical Spread';
            default: return '';
        }
    }

    public createComplexDescriptionCusip(order:any) {
        let complexDescriptionCusip = '';
        for ( let i: number = 0; i < order.optionLegList?.length; i++ ) {
            complexDescriptionCusip += this.createSecurityDescriptionFromQuote(order.optionLegList[i].quote,order.optionLegList[i].strikePrice).trim() + " (" + order.optionLegList[i].quote.cusipId + ")"+linebreak;
        }
        return complexDescriptionCusip;
    }

    public setComplexGlobals(order:any,account:Account,entitlements:TradingEntitlements,origOrder?:any) {
        // The expiration date and strike price were injected on the order's quote prior to opening the review
        // dialog. We need to defend against the values being modified after submit by storing them locally, since
        // actual quotes from the back-end never populate these fields and the server will alter at least the date
        // due to time zone. Maybe not 100% necessary for strike price, but as it's a field sent over from the main
        // form and not part of actual data submitted to the back-end, there's no harm in storing it locally.
        //
        // Once we've stored it, we can null out those fields on the quote.
        for ( let i: number = 0; i < order.optionLegList?.length; i++ ) {
            this.optionLegQuoteDesc[i] = this.createSecurityDescriptionFromQuote(order.optionLegList[i].quote,order.optionLegList[i].strikePrice);
            this.optionLegQuoteCusip[i] = order.optionLegList[i].quote.cusipId;
            this.optionLegChainTypes[i] = order.optionLegList[i].quote.callOrPutCd === 'C' ? 'call' : 'put';
            this.optionLegExpirationDates[i] = order.optionLegList[i].quote.optExpnDt;
            this.optionLegStrikePrices[i] = order.optionLegList[i].quote.optStrikePrc;
            this.optionLegQty[i] = this.splitRegExUnFixed(order.optionLegList[i].ordQty);
            this.sharesOrContractsLabel(order,i);
            this.optionLegTransactionTypes[i] = this.getTransactionType(order.optionLegList[i].transactionType,account,entitlements);
            this.optionLegSymbol[i] = order.optionLegList[i].quote.symId;
            this.getAccountTypeComplex(order,i);
            if (!!origOrder?.ordDtlKey) {
                this.origOptionLegQty[i] = this.splitRegExUnFixed(origOrder.optionLegList[i].ordQty);
                this.sharesOrContractsLabel(order,i,true);
                if (!this.hasQuantityEdited && origOrder.optionLegList[i].ordQty !== order.optionLegList[i].ordQty ) {
                    this.hasQuantityEdited = true;
                }
            }
        }
    }

    private sharesOrContractsLabel(order:any,i:number,origArray?:boolean) {
        let buyWriteMarriedPut = ['Buy-Write','Married-Put'].includes(this.getStrategy(order));
        if (buyWriteMarriedPut){
            if(!!origArray) {
                this.origOptionLegQty[i] += !i?' contract(s)':' shares';
            } else {
                this.optionLegQty[i] += !i?' contract(s)':' shares';
            }
        }
    }



    private createSecurityDescriptionFromQuote(quote: Quote,strikePrice:string): string {
        // If the quote is for an equity (i.e., Buy-Write or Married Put order),
        // just return the security description string
        if ( quote.securityTypeEnum === SecurityType.EQUITY ) {
            return quote.description.toString().replace(/\t/g, '').split(linebreak).toString();
        }

        // Must be an option quote, so build a detailed description matching
        // mainframe PROM output in order details, which is displayed in the
        // Cancel Order dialog.
        const callPutString = quote.callOrPutCd === 'C' ? 'CALL ' : 'PUT ';
        const sharesPerContract = quote.optShrPerContrCnt + ' ';
        const underlyingDesc = quote.description.trim() + ' ';
        const expDate = moment_(quote.optExpnDt).format('MM-DD-YY');
        const strikePrc = (+quote.optStrikePrc)?.toFixed(3);
        const optRoot = quote.optRootSymId;

        return callPutString + sharesPerContract + underlyingDesc + expDate + '@' + strikePrc + ' OPTION ROOT = ' + optRoot + ' ';
    }

    public getExpiryDate(order:any) {
        let expDate = !!order.quote.optExpnDt?order.quote.optExpnDt:order.quote.expirationDate;
        return moment_(expDate).format('MMM D, YYYY');
    }



    public getQuoteDescription(order: any): string {
        let _quoteDescription = "";
        if (!!order.quote?.description) {
            // Build a detailed description matching mainframe PROM output in order details,
            // which is displayed in the
            // Cancel Order dialog.
            const callPutString = order.quote.callOrPutCd === 'C' ? 'CALL ' : 'PUT ';
            const sharesPerContract = order.quote.optShrPerContrCnt + ' ';
            const underlyingDesc = order.quote.description.trim() + ' ';
            let date = !!order.quote.optExpnDt?order.quote.optExpnDt:order.quote.expirationDate;
            const expDate = moment_(date).format('MM-DD-YY');
            const strikePrc = (+order.quote.strikePrice)?.toFixed(3);
            const optRoot = order.quote.optRootSymId;

            return callPutString + sharesPerContract + underlyingDesc + expDate + '@' + strikePrc + ' OPTION ROOT = ' + optRoot + ' ';
        }
        return _quoteDescription;
    }
    
    public getQuoteDescriptionCusip(order: any): string {
        let _quoteDescriptionCusip = "";
        let _quoteDescription = "";
        if (!!order.quote?.cusipId) {
            _quoteDescriptionCusip = this.getQuoteDescription(order).trim() + " (" + order.quote.cusipId + ")"
        }
    
        return _quoteDescriptionCusip;
    }

    public getFormattedTrailer(order: any, entitlements:TradingEntitlements): string {
        let _orderTrailer = order.trailer === null ? '' : order.trailer;
        let _formattedTrailer = "";
        if(entitlements.isRiaUser && (!!order.trailer || !!order.trailerDiscExercised)) {

            if (order.trailerDiscExercised != null && order.trailerDiscExercised === 'Y') {
                _formattedTrailer = 'Discretion Exercised ' + _orderTrailer;
            } else if (order.trailerDiscExercised != null && order.trailerDiscExercised === 'N') {
                _formattedTrailer = 'Discretion Not Exercised ' + _orderTrailer;
            } else {
                _formattedTrailer = _orderTrailer;
            }
        }
        return _formattedTrailer;

    }

    public getQuantity(order: any): string {
        let _quantity = "";
        _quantity = this.splitRegExUnFixed(order.ordQty);
        return _quantity;
    }

    public getCustFirmInd(order:any,entitlements:TradingEntitlements,account:Account) {
        let custOrFirmInd = '';
        if (entitlements.isBdUser && (account && account.acctClassificationCd && account.acctClassificationCd.toUpperCase() === 'OM')) {
            if (order.custOrFirmInd?.toUpperCase() === 'C' || order.intlShrtMktAmt?.toUpperCase() === 'CUSTOMER') {
                custOrFirmInd = 'Customer';
            } else {
                custOrFirmInd = 'Broker/Dealer';
            }
        }
        return custOrFirmInd;
    }

    private showSolicited(entitlements: TradingEntitlements, account:Account): boolean {
        let showSolicited = false;
        if (entitlements.isBdUser) {
            showSolicited = true;
        } else if (!entitlements.isBdUser && account.ibdId === 'C01' && account.acctProdtProfileCd === 'PMAS') {
            showSolicited = true;
        } else if(entitlements.isRetailUser) {
            showSolicited = entitlements.isSolicitedEnabled || entitlements.isNonSolicitedEnabled;
        }
        return showSolicited;
    }

    public getSolicited(solicited: boolean, entitlements: TradingEntitlements, account: Account): string {
        let _solicited = "";
        if (this.showSolicited(entitlements,account)) {
            if (solicited) {
                _solicited = "Yes";
            } else {
                _solicited = "No";
            }
        }
        return _solicited;
    }

    public processDuration(duration: OrderDuration, gtdDate: string) {
        if (!!duration && duration === OrderDuration.GTD) {
            return 'GTDate - ' + gtdDate.replace(/-/gi,'/');
        } 

            switch(duration) {
                case OrderDuration.DAY:
                    return "Day";
                case OrderDuration.GTC:
                    return "Good till Cancelled (GTC)";
                case OrderDuration.PREMKT:
                    return "Pre-Market";
                case OrderDuration.AFTMKT:
                    return "After Market";
                case OrderDuration.OPG:
                    return "Execute at Opening (OPG)";
                case OrderDuration.IOC:
                    return "Execute Now or Cancel (IOC)";
                case OrderDuration.FOK:
                    return "Fill or Kill (FOK)";
                case OrderDuration.GTM:
                    return "Good till Month (GTM)";
                default:
                    return "";
            }
        
    }

    public setFormattedTrailer(order:any) {
        let orderTrailer = getValueOrEmptyString(order.trailer);
        if (order.trailerDiscExercised != null) {
          switch (order.trailerDiscExercised) {
            case 'Y':
              if (!orderTrailer.includes(TradingConstants.DISC_EXERCISE)) {
                orderTrailer = TradingConstants.DISC_EXERCISE + ' ' + orderTrailer;
              }
              break;
            case 'N':
              if (!orderTrailer.includes(TradingConstants.DISC_NOT_EXERCISE)) {
                orderTrailer = TradingConstants.DISC_NOT_EXERCISE + ' ' + orderTrailer;
              }
              break;
            default:
          }
        }
        return orderTrailer;
    }

    public processCommissionAmt(commType: string, commissionAmt: number) {
        let dollarCent = '';
        let commAmt = '';


        if (commType === 'COM' || commType === 'MUD') {
            dollarCent+='$';
        } else if (commType==='RTE') {
            dollarCent+= '\u00A2';
        }

        commAmt += dollarCent;

        if (commType === 'PFM' || commType === 'MLT') {
            commAmt += commissionAmt;
        } else {
            commAmt += this.splitRegExFixed(commissionAmt);
        }

        if (commType === 'PFM' || commType === 'MLT') {
            commAmt+='%';
        }
        return commAmt;
    }

    public getAccountType(order: any): string {
        let accountType;
        if (!!order.avgPrcAcct && !!order.avgPrcAcctTyp) {
            accountType = order.avgPrcAcctTyp;
        } else {
            accountType = order.accountType;
        }
        switch (accountType) {
            case AccountType.CASH:
                return "Cash";
            case AccountType.MARGIN:
                return "Margin";
            case AccountType.SHORT:
                return "Short";
            case AccountType.COD:
                return "COD";
            case AccountType.RETIREMENT:
                return "Retirement";
            case AccountType.PRINCIPAL:
                return "Principal";
            case AccountType.INCOME:
                return "Income";
            default:
                return "";
        }

    }

    private checkPartialOrder(orderStatus:string) {
        return orderStatus === 'PART_EXECUTED' || orderStatus === 'PART_MIXED' || orderStatus === 'PART_CANCELED';
    }

    public getHeadersEditOrder(order:any) {
        let orderStatus = (order.rawStatus || '').toString().trim();
        let strategy = order.strategy;
        let isPartialOrder = this.checkPartialOrder(orderStatus);
        
        let label1 = '';
        let contractsChangedToLabel = '';
        if ( ['Buy-Write','Married-Put'].includes(strategy)) {
            contractsChangedToLabel = 'Quantity Changed To';
            let originalContractsLabel = 'Original Quantity';
            let remainingContractsLabel = 'Remaining Quantity';
            if (isPartialOrder) {
                label1 = remainingContractsLabel;
            } else {
                label1 = originalContractsLabel;
            }
        } else {
            contractsChangedToLabel = 'Contracts Changed To';
            let originalContractsLabel = 'Original Contracts';
            let remainingContractsLabel = 'Remaining Contracts';
            if (isPartialOrder) {
                label1 = remainingContractsLabel;
            } else {
                label1 = originalContractsLabel;
            }
        }
        return [label1,contractsChangedToLabel]
    }

    public getHeader(order:any) { 
        let orderStatus = (order.rawStatus || '').toString().trim();
        let strategy = order.strategy;
        let isPartialOrder = this.checkPartialOrder(orderStatus);
        
        let contractsLabel = '';
        if ( ['Buy-Write','Married-Put'].includes(strategy)) {
            contractsLabel = isPartialOrder ? 'Original Quantity': 'Quantity';
        } else {
            contractsLabel = isPartialOrder ? 'Original Contracts':'Contracts';
        }
        return contractsLabel;
    }

    public getAccountTypeComplex(order: any,i:number) {
        let accountType:any;
        if (!!order.avgPrcAcct && !!order.avgPrcAcctTyp) {
            accountType = order.avgPrcAcctTyp;
        } else {
            if ( order.strategy === OptionStrategy.SINGLE ) {
                this.translateService.get(order.optionLegList[0].accountType).subscribe((text) => {
                    accountType = text;
                });
                this.optionLegAccountTypes[0] = accountType;
                return;
            } else {
                this.translateService.get(order.optionLegList[i].accountType).subscribe((text) => {
                    accountType = text;
                });
                this.optionLegAccountTypes[i] = accountType;
                return;
            }
        }
        switch (accountType) {
            case AccountType.CASH:
                return "Cash";
            case AccountType.MARGIN:
                return "Margin";
            case AccountType.SHORT:
                return "Short";
            case AccountType.COD:
                return "COD";
            case AccountType.RETIREMENT:
                return "Retirement";
            case AccountType.PRINCIPAL:
                return "Principal";
            case AccountType.INCOME:
                return "Income";
            default:
                return "";
        }

    }

    public getAccountTypeEditOrder(origOrder:any): string {
        if (!!origOrder.avgPrcAcct && !!origOrder.avgPrcAcctTyp) {
            origOrder.accountType = origOrder.avgPrcAcctTyp;
        }
        switch (origOrder.accountType) {
            case AccountType.CASH:
                return "Cash";
            case AccountType.MARGIN:
                return "Margin";
            case AccountType.SHORT:
                return "Short";
            case AccountType.COD:
                return "COD";
            case AccountType.RETIREMENT:
                return "Retirement";
            case AccountType.PRINCIPAL:
                return "Principal";
            case AccountType.INCOME:
                return "Income";
            default:
                return "";
        }
        
    }

    public getCommType(commType) {
        switch (commType) {
            case CommType.COM:
                return "Override";
            case CommType.MLT:
                return "Multiplier";
            case CommType.MUD:
                return "Markup / Markdown";
            case CommType.MUD_AUTO:
                return "Markup / Markdown - Auto";       
            case CommType.PFM:
                return "Percent of First Money"; 
            case CommType.RTE:
                return "Rate Per Share";    
            default:
                return "";
        }
    }

    public getAddlOrdData(addlOrdData: any): string {
        let _addlOrdData = "";
        if (!!addlOrdData) {
            for (let tradeCondition of addlOrdData) {
                _addlOrdData = _addlOrdData + this.getTradeCondition(tradeCondition) + ",";
            }
        }
        return _addlOrdData.substring(0,_addlOrdData.length-1);
    }
    
    private getTradeCondition(tradeCondition: string,): string {
        switch (tradeCondition) {
            case "AON":
                return "All Or None";
            case "NOTHELD":
                return "Not Held";
            case "DONOTINCREASE":
                return "Do Not Increase Shares";
            case "DONOTREDUCE":
                return "Do Not Reduce Price";
            case "NEXTDAY":
                return "Next Day";
            case "CASHSAMEDAY":
                return "Cash - Same Day Settlement";
            case "EXECUTEASAGENT":
                return "Execute As Agent";
            default:
                return "";
        }
    }

    public getFloorInst(floorInst: string): string {
        switch (floorInst) {
            case "10% POV": return "10% Percentage of Volume (POV)";
            case "15% POV": return "15% Percentage of Volume (POV)";
            case "25% POV": return "25% Percentage of Volume (POV)";
            case "33% POV": return "33% Percentage of Volume (POV)";
            case "EXECUTE HALF NOW,WRK HALF": return "Execute half now, work half";
            case "NO NEED TO COMPLETE": return "No need to complete";
            case "PLS WAIT FOR FURTHER INST": return "Please wait for further instructions";
            case "TWAP": return "Time Weighted Average Price (TWAP)";
            case "VWAP": return "Volume Weighted Average Price (VWAP)";
            case "VWAP OVER 2 HOURS": return "Volume Weighted Avg Price > 2 hours (VWAP)";
            case "VWAP OVER 3 HOURS": return "Volume Weighted Avg Price > 3 hours (VWAP)";
            case "WORK NOT HELD": return "Work Not Held";
            case "INSIDER ACCOUNT": return "Insider account";
            case "NON REPORT INSIDER/ASSOC": return "Non report insider/assoc";
            case "PROMOTOR INSIDER RELATION": return "Promotor Insider Relation";
            case "REPORTING INSIDER": return "Reporting insider";
            case "SIGNIFICANT SHRHLDR(>20%)": return "Significant Shareholder (>20%)";
            default:
                return "";
        }
    }

    public getOrderType(orderType): string {
        switch (orderType) {
            case OrderType.MARKET:
                return "Market";
            case OrderType.LIMIT:
                return "Limit";
            case OrderType.STOP:
                return "Stop";
            case OrderType.AON:
                return "AON";
            case OrderType.FOK:
                return "FOK";
            case OrderType.STOP_LIMIT:
                return "Stop Limit";
            case OrderType.STOP_LIMIT_AON:
                return "Stop Limit AON";
            case OrderType.TRAILING_STOP_DOLLAR:
                return "Trailing Stop $";
            case OrderType.TRAILING_STOP_PCT:
                return "Trailing Stop %";
            case OrderType.TRAILING_STOP_LIMIT_DOLLAR:
                return "Trailing Stop Limit $";
            case OrderType.TRAILING_STOP_LIMIT_PCT:
                return "Trailing Stop Limit %";
            case OrderType.TRAILING_STOP_LIMIT_AON:
                return "Trailing Stop Limit AON";
            case OrderType.TRAILING_STOP_LIMIT_PCT_AON:
                return "Trailing Stop Limit % AON";
            case OrderType.EXECUTE_AT_CLOSE:
                return "Execute at Close";
            case OrderType.LIMIT_ON_CLOSE:
                return "Limit on Close";
            case OrderType.OR_BETTER:
                return "Or Better";
            case OrderType.WOW:
                return "WOW";    
            case OrderType.NET_CREDIT:
                return "Net Credit";  
            case OrderType.NET_DEBIT:
                return "Net Debit";
            case OrderType.EVEN:
                return "Even";                 
            default:
                return "";
        }
    }

    public getSourceOfInput(sourceOverride: string): string {
        switch (sourceOverride) {
            case "3":
                return "NetX360";
            case "I":
                return "Web";
            case "J":
                return "Interactive Voice Response";
            case "K":
                return "Proprietary";
            case "Z":
                return "Home Office";
            case "Q":
                return "Wireless Client via NX";
            case "R":
                return "Wireless Pro via NX";
            case "A":
                return "External Advisor Site";
            case "5":
                return "External Mobile Site";
            default:
                return "";
        }
    }

    formatBackendTime(sourceTime: string): string { //aabbcccccc -> aa:bb:ccccc
        if ( !!sourceTime && sourceTime !== '' ) {
            return sourceTime.substring(0, 2) + ':' + sourceTime.substring(2, 4) + ':' + sourceTime.substring(4);
        } else {
            return sourceTime;
        }
    }

    formatBackendDate(sourceDate: string, origOrder?:TradingOrderBase): string {     // yyyymmdd => mm/dd/yyyy
        if (origOrder?.orderAction === ORDER_ACTIONS.CORRECT && !!sourceDate) {
            return sourceDate.substring(4, 6) + '/' + sourceDate.substring(6, 8) + '/' + sourceDate.substring(0, 4);
        } else if(!!sourceDate){
            return sourceDate
        } else {
            return '';
        }
      }

    public getDiscExercised(discExercised: string, entitlements: TradingEntitlements): string {
        let _discExercised = "";
        if (!entitlements.isRetailUser) { 
            if (discExercised === "Y") {
                _discExercised = "Yes";
            } else if (discExercised === "N"){
                _discExercised = "No";
            }
        } 
        return getValueOrEmptyString(_discExercised);

    }

    public getDeskRoute(oatsDeskTypeCode: string): string {
        switch (oatsDeskTypeCode) {
            case "A": return "Agency(A)";
            case "AR": return "Arbitrage(AR)";
            case "B": return "Block Trading(B)";
            case "C": return "Convertibles Desk(C)";
            case "D": return "Derivatives(D)";
            case "IS": return "Institutional(IS)";
            case "IN": return "International(IN)";
            case "O": return "Other(O)";
            case "PF": return "Preferred(PF)";
            case "PT": return "Program Trading(PT)";
            case "PR": return "Proprietary(PR)";
            case "S": return "Sales(S)";
            case "T": return "Trading(T)";
            case "TR": return "Treasury(TR)";
            case "SW": return "Swaps(SW)";
            case "EC": return "Eqty Capital Mkts(EC)";
            case "CR": return "Ctrl Risk Books(CR)";
            default:
                return "";
        }
    }

    public getPreparedBy(username:string): string {
        let today = new Date().toISOString().slice(0, 10);
        let _preparedBy:string;

        if(!!username) {
            _preparedBy = "Prepared by "+ username +" on " + today; 

        } else {
            _preparedBy = 'Prepared on ' + today;
        }
        
        return _preparedBy;
    }

    public getPaInd(paInd:string) {
        let _paInd;
        if (paInd.toUpperCase()==='A') {
            _paInd = 'Agent'
        } else if (paInd.toUpperCase()==='P') {
            _paInd = 'Principal'
        } else {
            _paInd = 'Agent';
        }
        return _paInd
    }

    public showCatEntity(account:Account,entitlements:TradingEntitlements,order:TradingOrderBase) {
        if (['AP','CO','FA','IA','IN','OB'].includes(account.acctClassificationCd) && entitlements.isEntitledtToShowCatEntity) {
            return order.orderAction === ORDER_ACTIONS.CHANGE ? false : true;
        }
    }
}